import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  getOrderResponse,
  getSelectedExhibitionId,
  getSelectedStep,
  getSelfRegistration,
  selectOrderedStepValidities
} from '@app/app.reducer';
import { FormName } from '@core/data-access/models/form/form.model';
import { BaseRoute, StepRoute } from '@core/data-access/models/routes.model';
import { InitializationService } from '@core/data-access/models/service.model';
import { FormValidityService } from '@core/data-access/services/form/form-validity.service';
import { Store } from '@ngrx/store';
import { AppConstants } from '@shared/app-constants';
import { FormStatusPayloadModel, OrderedStepValidities, StepName } from '@store/step-forms/step.interface';
import { SetFormValidity, SetSelectedStep, SetStepFormsValidity } from '@store/step-forms/steps-forms.actions';
import { setStepFormValidities } from '@store/step-forms/utils/step-validities';
import { mapStepNameToStepRoute } from '@store/step-forms/utils/step-validity-mappers';
import { filter as _filter, every, find, findIndex, get, includes, isNil, isNull, slice } from 'lodash';
import { combineLatest, distinctUntilChanged, filter, map, share } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FormStepValidityService implements InitializationService {
  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #store = inject(Store);
  readonly #formValidityService = inject(FormValidityService);

  #checkStepValidity$ = this.checkStepValidity$();
  #shouldInvalidateCurrentStep$ = this.shouldInvalidateCurrentStep$();

  #isInvalidBuyerInformationStepModalOpen = signal(false);

  #selectedExhibitionId = this.#store.selectSignal(getSelectedExhibitionId);
  #baseRoute = computed(() =>
    this.#store.selectSignal(getSelfRegistration)() ? BaseRoute.SelfRegistration : BaseRoute.WebShop
  );

  init() {
    this.initCheckBuyerInformationSyncFormStepValiditySubscription();
    this.initCheckBuyerQuestionnaireSyncFormStepValiditySubscription();
    this.initTicketAssignmentFormsAndFormFieldsValiditySubscription();
    this.initStepValidityRedirectionSubscription();
    this.initBuyerInformationStepValidityOpenModalSubscription();
  }

  redirectToBuyerInformationStep() {
    this.#isInvalidBuyerInformationStepModalOpen.set(false);

    this.#store.dispatch(new SetSelectedStep(StepName.BuyerInformation));

    this.#router.navigate([`/${this.#baseRoute()}/${this.#selectedExhibitionId()}/${StepRoute.BuyerInformation}`], {
      state: { redirectFromRoute: StepRoute.TicketAssignment }
    });
  }

  getIsInvalidBuyerInformationStepModalOpen() {
    return this.#isInvalidBuyerInformationStepModalOpen;
  }

  getShouldInvalidateCurrentStep$() {
    return this.#shouldInvalidateCurrentStep$;
  }

  private initCheckBuyerInformationSyncFormStepValiditySubscription() {
    return combineLatest({
      buyerInformationFormsAndFormFieldsValidity:
        this.#formValidityService.getCheckBuyerInformationFormsAndFormFieldsValidity$(),
      selectedStep: this.#store.select(getSelectedStep),
      orderResponse: this.#store.select(getOrderResponse)
    })
      .pipe(
        filter(
          ({ selectedStep, orderResponse }) => selectedStep !== StepName.BuyerInformation && isNull(orderResponse)
        ),
        distinctUntilChanged((previousValue, currentValue) => {
          const areAllPreviousBuyerInformationStepFormsValid = every(
            previousValue.buyerInformationFormsAndFormFieldsValidity,
            'isValid'
          );
          const areAllCurrentBuyerInformationStepFormsValid = every(
            currentValue.buyerInformationFormsAndFormFieldsValidity,
            'isValid'
          );

          return areAllPreviousBuyerInformationStepFormsValid === areAllCurrentBuyerInformationStepFormsValid;
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(({ buyerInformationFormsAndFormFieldsValidity }) => {
        this.#store.dispatch(
          new SetStepFormsValidity({
            stepName: StepName.BuyerInformation,
            formValidities: setStepFormValidities(buyerInformationFormsAndFormFieldsValidity, false)
          })
        );
      });
  }

  private initCheckBuyerQuestionnaireSyncFormStepValiditySubscription() {
    return combineLatest({
      buyerQuestionnaireFormsAndFormFieldsValidity:
        this.#formValidityService.getCheckBuyerQuestionnaireFormAndFormFieldsValidity$(),
      selectedStep: this.#store.select(getSelectedStep),
      orderResponse: this.#store.select(getOrderResponse)
    })
      .pipe(
        filter(
          ({ selectedStep, orderResponse }) => selectedStep !== StepName.BuyerQuestionnaire && isNull(orderResponse)
        ),
        distinctUntilChanged((previousValue, currentValue) => {
          const areAllPreviousBuyerQuestionnaireStepFormsValid = every(
            previousValue.buyerQuestionnaireFormsAndFormFieldsValidity,
            'isValid'
          );
          const areAllCurrentBuyerQuestionnaireStepFormsValid = every(
            currentValue.buyerQuestionnaireFormsAndFormFieldsValidity,
            'isValid'
          );

          return areAllPreviousBuyerQuestionnaireStepFormsValid === areAllCurrentBuyerQuestionnaireStepFormsValid;
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(({ buyerQuestionnaireFormsAndFormFieldsValidity }) => {
        this.#store.dispatch(
          new SetStepFormsValidity({
            stepName: StepName.BuyerQuestionnaire,
            formValidities: setStepFormValidities(buyerQuestionnaireFormsAndFormFieldsValidity, false)
          })
        );
      });
  }

  private initTicketAssignmentFormsAndFormFieldsValiditySubscription() {
    return this.#formValidityService
      .getCheckSelectedTicketAssignmentFormsAndFormFieldsValidity$()
      .pipe(
        distinctUntilChanged(
          (previousFormValidity, currentFormValidity) =>
            previousFormValidity.areAllTicketAssignmentStepFormsValidAndValidationIsNotProcessing ===
            currentFormValidity.areAllTicketAssignmentStepFormsValidAndValidationIsNotProcessing
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(({ areAllTicketAssignmentStepFormsValidAndValidationIsNotProcessing }) => {
        const formValidityStatus: FormStatusPayloadModel = {
          formInfo: [StepName.TicketAssignment, FormName.TicketHolder],
          valid: areAllTicketAssignmentStepFormsValidAndValidationIsNotProcessing
        };

        this.#store.dispatch(new SetFormValidity(formValidityStatus));
      });
  }

  private initStepValidityRedirectionSubscription() {
    return this.#checkStepValidity$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(({ invalidStepName }) => {
      if (includes([StepName.BuyerInformation, StepName.BuyerQuestionnaire], invalidStepName)) {
        return;
      }

      this.#store.dispatch(new SetSelectedStep(invalidStepName));

      this.#router.navigate([
        `/${this.#baseRoute()}/${this.#selectedExhibitionId()}/${mapStepNameToStepRoute(invalidStepName)}`
      ]);
    });
  }

  private initBuyerInformationStepValidityOpenModalSubscription() {
    return this.#checkStepValidity$
      .pipe(
        filter(({ selectedStepName }) => selectedStepName !== StepName.BuyerInformation),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(({ invalidStepName }) => {
        this.#isInvalidBuyerInformationStepModalOpen.set(invalidStepName === StepName.BuyerInformation);
      });
  }

  private shouldInvalidateCurrentStep$() {
    return combineLatest({
      selectedStepName: this.#store.select(getSelectedStep),
      orderedStepValidities: this.#store.select(selectOrderedStepValidities)
    }).pipe(
      map(({ selectedStepName, orderedStepValidities }) => {
        const visibleSteps = _filter(orderedStepValidities, (step) => get(step, 'value.visible'));

        const currentStep = find(visibleSteps, (step) => step.name === selectedStepName);
        const firstInvalidStep = find(visibleSteps, (step) => !get(step, 'value.valid'));

        if (isNil(currentStep) || isNil(firstInvalidStep)) {
          return false;
        }

        return get(currentStep, 'value.order') > get(firstInvalidStep, 'value.order');
      }),
      share(),
      takeUntilDestroyed(this.#destroyRef)
    );
  }

  private checkStepValidity$() {
    const getFirstInvalidStepName = (selectedStep: StepName, orderedStepValidities: OrderedStepValidities) => {
      const visibleSortedStepValidities = _filter(orderedStepValidities, ({ value }) => value.visible);
      const selectedStepIndex = findIndex(visibleSortedStepValidities, { name: selectedStep });
      const stepValiditiesBeforeSelectedStep = slice(visibleSortedStepValidities, 0, selectedStepIndex);
      const firstInvalidStepIndex = findIndex(stepValiditiesBeforeSelectedStep, ({ value }) => !value.valid);

      if (firstInvalidStepIndex === AppConstants.INDEX_NOT_FOUND || firstInvalidStepIndex >= selectedStepIndex) {
        return null;
      }

      return visibleSortedStepValidities[firstInvalidStepIndex]?.name;
    };

    return combineLatest({
      selectedStepName: this.#store.select(getSelectedStep),
      orderedStepValidities: this.#store.select(selectOrderedStepValidities),
      orderResponse: this.#store.select(getOrderResponse)
    }).pipe(
      filter(({ orderResponse }) => !includes(this.#router.url, BaseRoute.Profile) && isNull(orderResponse)),
      map(({ selectedStepName, orderedStepValidities }) => ({
        selectedStepName,
        invalidStepName: getFirstInvalidStepName(selectedStepName, orderedStepValidities)
      })),
      filter(({ invalidStepName }) => !isNil(invalidStepName)),
      share(),
      takeUntilDestroyed(this.#destroyRef)
    );
  }
}
